import { useMemo, useCallback } from 'react';
import { Beer } from '../types';
import { useUserInteractions } from './useUserInteractions';

/**
 * Custom hook for filtering beers based on the active tab
 * 
 * This hook follows the Single Responsibility Principle by focusing only on
 * filtering beers
 * 
 * @param beers - The list of beers to filter
 * @param activeTab - The currently active tab ('0' = All Beers, '1' = My Drinks, '2' = Tasting List)
 * @param showOnlyFavorites - Optional filter to show only favorites (primarily affects My Drinks tab)
 * @param filterByFavorites - Optional filter to show only favorites within Tried Beers list
 * @returns Filtered list of beers based on the active tab
 */
export function useTabFiltering(beers: Beer[], activeTab: string, showOnlyFavorites: boolean = false, filterByFavorites: boolean = false) {
  const { userInteractions } = useUserInteractions();
  
  // Helper function to check if a beer has been interacted with (rated, noted, favorited, or marked as tried)
  // Wrapped in useCallback to prevent it from changing on every render
  const hasBeerBeenTried = useCallback((beerId: string): boolean => {
    // A beer is considered "tried" if it has been:
    // 1. Added to the tried list, OR
    // 2. Added to favorites, OR
    // 3. Given a rating (greater than 0), OR
    // 4. Has notes added
    
    const isInTriedList = (userInteractions.triedList || []).includes(beerId);
    const isFavorited = userInteractions.favorites.includes(beerId);
    const hasRating = userInteractions.ratings[beerId] > 0;
    const hasNotes = userInteractions.notes[beerId]?.trim() !== '' && 
                     userInteractions.notes[beerId] !== undefined;
    
    return isInTriedList || isFavorited || hasRating || hasNotes;
  }, [userInteractions]);
  
  // Filter beers based on the active tab
  const filteredBeers = useMemo(() => {
    if (!beers || beers.length === 0) {
      return [];
    }
    
    // Create Sets for O(1) lookups
    const tastingListSet = new Set(userInteractions.tastingList);
    const favoritesSet = new Set(userInteractions.favorites);
    
    // Apply tab-specific filtering
    switch (activeTab) {
      case '1': // My Beers tab
        // Toggle between "Tried Beers" (showOnlyFavorites = false) and "Tasting List" (showOnlyFavorites = true)
        // Tried Beers: Show only beers that have been tried (rated, noted, or favorited) - exclude tasting list
        // Tasting List: Show only beers in the tasting list
        return beers.filter(beer => {
          const tried = hasBeerBeenTried(beer.id);
          const inTastingList = tastingListSet.has(beer.id);
          const isFavorite = favoritesSet.has(beer.id);
          
          // If showOnlyFavorites is true, show only tasting list beers
          if (showOnlyFavorites) {
            return inTastingList;
          }
          
          // If showOnlyFavorites is false, show only tried beers (excluding tasting list)
          // Additionally, if filterByFavorites is true, only show favorites
          if (filterByFavorites) {
            return tried && !inTastingList && isFavorite;
          }
          
          return tried && !inTastingList;
        });
        
      case '2': // Tasting List tab
        // Show only beers in the tasting list that have NOT been tried
        // This follows the requirement that Tasting List should only show beers
        // the user intends to try but hasn't tried yet
        return beers.filter(beer => 
          tastingListSet.has(beer.id) && !hasBeerBeenTried(beer.id)
        );
        
      case '0': // All Beers tab (default)
      default:
        // Show all beers
        return [...beers];
    }
  }, [beers, activeTab, userInteractions, hasBeerBeenTried, showOnlyFavorites, filterByFavorites]);
  
  return Object.assign(filteredBeers, { hasBeerBeenTried });
}
